import { useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useMfaVerify } from "@/lib/hooks/auth";
import type { UserRead } from "@/lib/schemas/users";
import {
  MfaVerifyContext,
  type MfaPromptOptions,
  type MfaVerifyContextValue,
} from "@/lib/hooks/mfa-verify-prompt";

/**
 * Global MFA verify dialog (Phase 10F strict MFA gate).
 *
 * Quando un endpoint risponde 403 + `kind: "mfa_required"`, l'API client invoca
 * `promptMfa(retryFn)` esposto dal context. Il dialog mostra l'input TOTP,
 * verifica via POST /auth/mfa/verify, poi:
 * - se l'utente passa un onVerified callback: lo invoca (best-effort re-trigger
 *   della action originale).
 * - altrimenti: solo close (user dovra' re-cliccare la action).
 *
 * Variante **not-enrolled**: se l'utente non ha ancora una TOTP locale
 * (`mfa_enrolled === false` nella cache di /users/me), chiedere un codice e'
 * un cul-de-sac — `POST /auth/mfa/verify` risponde `MFA TOTP not enrolled`
 * finche' non esiste un secret. In quel caso il dialog mostra un rimando a
 * /settings invece dell'input. Post-Track B il caso e' la norma per un
 * account-admin col sentinel `member` (nessun banner sul ruolo lo copriva) e,
 * con l'OTP abolito sull'IdP, la TOTP locale e' l'unico secondo fattore.
 *
 * Il flag si legge dalla CACHE (`getQueryData`), non con una query: il
 * provider e' montato sopra il RouterProvider (anche sulla login pubblica) e
 * un fetch di /users/me qui produrrebbe 401 su ogni pagina anonima. Quando il
 * dialog si apre l'utente e' autenticato e la cache e' gia' popolata; cache
 * vuota → si degrada al comportamento con input (nessuna regressione).
 *
 * Context + hook + helper sono in lib/hooks/mfa-verify-prompt.ts per separare
 * react-refresh-friendly component file da non-component exports.
 */
export function MfaVerifyProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const onVerifiedRef = useRef<(() => void) | undefined>(undefined);
  const onCancelledRef = useRef<(() => void) | undefined>(undefined);
  const [code, setCode] = useState("");
  const [verifySuccessCount, setVerifySuccessCount] = useState(0);
  const mutation = useMfaVerify();
  const queryClient = useQueryClient();
  // Letto al render (il cambio di `open` ne provoca uno): nessuna subscription,
  // nessun fetch. Vedi docstring.
  const needsEnrollment =
    open && queryClient.getQueryData<UserRead>(["auth", "me"])?.mfa_enrolled === false;

  const promptMfa = useCallback((opts?: MfaPromptOptions): void => {
    // If a prompt is already pending (concurrent mfa_required), cancel the
    // previous one so its awaiting Promise rejects rather than hanging.
    onCancelledRef.current?.();
    onVerifiedRef.current = opts?.onVerified;
    onCancelledRef.current = opts?.onCancelled;
    setCode("");
    mutation.reset();
    setOpen(true);
  }, [mutation]);

  // Close = dismiss/cancel: fire onCancelled (backlog 8.4) so the caller's
  // pending Promise rejects. The success path clears onCancelledRef first.
  const close = useCallback((): void => {
    const onCancel = onCancelledRef.current;
    onVerifiedRef.current = undefined;
    onCancelledRef.current = undefined;
    setOpen(false);
    setCode("");
    mutation.reset();
    if (onCancel) onCancel();
  }, [mutation]);

  const onSubmit = useCallback(async (): Promise<void> => {
    if (code.length < 6) return;
    try {
      await mutation.mutateAsync({ code });
      const cb = onVerifiedRef.current;
      onVerifiedRef.current = undefined;
      onCancelledRef.current = undefined; // success → close() must NOT fire cancel
      // Incrementa il counter: i form osservano via `useResetMfaErrorOnVerify`
      // e resettano le proprie mutation in errore mfa_required, eliminando
      // il box rosso stale sotto il form (vedi mfa-verify-prompt.ts).
      setVerifySuccessCount((c) => c + 1);
      close();
      // backlog 8.4: re-run the original action transparently (apiRequest's
      // _retryAfterMfa resolves the original Promise with the retry result).
      if (cb) cb();
    } catch {
      // mutation.error e' mostrato nel dialog; user puo' ri-tentare
    }
  }, [code, mutation, close]);

  const value = useMemo<MfaVerifyContextValue>(
    () => ({ promptMfa, verifySuccessCount }),
    [promptMfa, verifySuccessCount],
  );

  return (
    <MfaVerifyContext.Provider value={value}>
      {children}
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
              {t("mfa.verify_required.title")}
            </DialogTitle>
            <DialogDescription>
              {needsEnrollment
                ? t("mfa.verify_required.not_enrolled_description")
                : t("mfa.verify_required.description")}
            </DialogDescription>
          </DialogHeader>

          {needsEnrollment ? (
            <p className="pt-1 text-xs text-muted-foreground">
              {t("mfa.verify_required.not_enrolled_hint")}
            </p>
          ) : (
            <div className="space-y-2 pt-1">
              <Label htmlFor="mfa_code" className="text-xs font-medium">
                {t("mfa.verify_required.code_label")}
              </Label>
              <Input
                id="mfa_code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && code.length >= 6) {
                    e.preventDefault();
                    void onSubmit();
                  }
                }}
                placeholder="123456"
                className="text-center font-mono text-lg tracking-widest"
                disabled={mutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                {t("mfa.verify_required.hint")}
              </p>
            </div>
          )}

          {mutation.isError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              <ul className="ml-4 list-disc space-y-0.5">
                {formatApiError(mutation.error).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={close}
              disabled={mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            {needsEnrollment ? (
              <Button asChild type="button">
                <a href="/settings">{t("mfa.verify_required.not_enrolled_cta")}</a>
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => void onSubmit()}
                disabled={mutation.isPending || code.length < 6}
              >
                {mutation.isPending && (
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />
                )}
                {t("mfa.verify_required.submit")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MfaVerifyContext.Provider>
  );
}
